import { fourFConfig, schemaMeaning, modeMeaning, moodLevel, moodColor } from "./config";
import { overrideTrend } from "./overrideTrend";
import { allNeedLists, latestIdeas } from "./needs";
import { CORE_NEEDS, COPING_STYLES, copingStyleLabel, flipStats, needLabel, schemaScript } from "./schemaFlip";
import dayjs from "dayjs";
import { themeGrid, recoveryStats, fmtMins } from "./triggerInsights";
import { missingSections } from "./sections";
const STATE_HEX = {
  fight: "#E89B8C",
  flight: "#E6C288",
  freeze: "#7FB5C4",
  fawn: "#A6A8E0"
};
function fmt(iso) {
  const d = new Date(iso);
  return d.toLocaleString(void 0, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function overrideTrendHtml(sessions) {
  const { used, trend, timed, maxMins, message } = overrideTrend(sessions);
  if (timed.length === 0) return "";
  const pre = used.reduce((a, s) => a + s.preSuds, 0) / used.length;
  const post = used.reduce((a, s) => a + s.postSuds, 0) / used.length;
  const bars = trend.map((t) => {
    const h = t.mins != null ? Math.max(6, Math.round(t.mins / maxMins * 110)) : 0;
    return `<td style="vertical-align:bottom;text-align:center;border:none;padding:0 6px">
        <div style="font-size:10px;color:#6b7280;margin-bottom:3px">${t.mins != null ? t.mins.toFixed(1) : ""}</div>
        <div style="height:${h}px;background:#5B8DEF;border-radius:4px 4px 0 0"></div>
        <div style="font-size:10px;color:#6b7280;margin-top:4px">${dayjs(t.m + "-01").format("MMM")}</div>
      </td>`;
  }).join("");
  return `
    <h2>Triggerific-Pilot override \u2014 minutes to settle by month</h2>
    <p class="lead">Average distress ${pre.toFixed(1)} \u2192 ${post.toFixed(1)} out of 10 across ${used.length} override session${used.length === 1 ? "" : "s"}.${message ? " " + message : ""}</p>
    <table style="width:100%;height:150px;border-collapse:collapse;table-layout:fixed;margin:6px 0 4px"><tr style="height:140px">${bars}</tr></table>`;
}
function notesHtml(notes, client) {
  if (!notes?.length) return "";
  const esc = (t) => String(t).replace(/</g, "&lt;");
  const d = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `<h2>Conversation with the psychologist</h2>
    <p class="lead">Notes left on shared reports, and ${esc(client || "the client")}'s replies (oldest first).</p>
    ${notes.map((n) => `<div class="conv"><div class="conv-m">${d(n.at)} &middot; ${esc(n.who || "Psychologist")} on the ${esc(n.title)}</div><div class="conv-t">${esc(n.text)}</div>${n.reply ? `<div class="conv-r"><div class="conv-m">${esc(client || "Client")} replied &middot; ${d(n.reply.at)}</div>${esc(n.reply.text)}</div>` : ""}</div>`).join("")}`;
}
function unfinishedBit(s) {
  const m = missingSections(s);
  return m.length ? `<div class="st-unfinished">Unfinished entry &mdash; not completed: ${m.map((x) => x.label).join(", ")}. Worth discussing in session.</div>` : "";
}
function unfinishedSummary(sessions) {
  const n = sessions.filter((s) => missingSections(s).length).length;
  return n ? `<div class="st-unfinished" style="font-size:14px;margin:0 0 18px">${n} entr${n === 1 ? "y was" : "ies were"} left unfinished &mdash; the client may have been interrupted or found these too hard to continue. Worth discussing in session (marked below).</div>` : "";
}
function triggerTrendsHtml(sessions) {
  const g = themeGrid(sessions);
  const r = recoveryStats(sessions);
  const esc = (t) => String(t).replace(/</g, "&lt;");
  const grid = g.rows.length ? `
    <h2>Trigger themes &mdash; average mood as a result of the trigger, by month</h2>
    <table style="table-layout:auto"><thead><tr><th>Theme</th>${g.cols.map((m) => `<th class="c-center">${dayjs(m + "-01").format("MMM")}</th>`).join("")}</tr></thead><tbody>
    ${g.rows.map((row) => `<tr><td><b>${esc(row.theme)}</b> (${row.n})</td>${row.cells.map((c) => `<td class="c-center" style="${c ? `background:${moodColor(Math.round(c.v))};color:#1B2A3B;font-weight:700` : ""}">${c ? c.v.toFixed(1) : "&middot;"}</td>`).join("")}</tr>`).join("")}
    </tbody></table>${g.hardest ? `<p class="lead" style="margin-top:8px">Hardest-hitting theme: <b>${esc(g.hardest.theme)}</b> (average mood ${g.hardest.avg.toFixed(1)}).</p>` : ""}` : "";
  const rec = r.count ? `
    <h2>Time to return to calm</h2>
    <p class="lead">Average ${fmtMins(r.avg)} from reaching out to calm distress (0&ndash;3) or neutral mood, across ${r.count} triggered check-in${r.count === 1 ? "" : "s"}. ${r.trend.filter((t) => t.mins != null).map((t) => `${t.label}: ${fmtMins(t.mins)}`).join(" &middot; ")}${r.message ? `<br/>${r.message}` : ""}</p>` : "";
  return grid + rec;
}
function flipHtml(entries, cards = [], sessions = []) {
  const needLists = allNeedLists(sessions, entries);
  if (entries.length === 0 && cards.length === 0 && !needLists.some((l) => l.length)) return "";
  const st = flipStats(entries);
  const esc = (t) => t.replace(/</g, "&lt;");
  const top = st.topSchemas.map(([k, v]) => `<span class="chip" style="background:#DCE8F8;color:#2F5A96;">${k}: ${v} &rarr; ${schemaScript(k)?.adaptive ?? ""}</span>`).join("");
  const styles = COPING_STYLES.map((c) => `${c.label}: ${st.styles[c.key]}`).join(" &middot; ");
  const ticked = needLists.flat();
  const needCounts = {};
  ticked.forEach((n) => needCounts[n] = (needCounts[n] ?? 0) + 1);
  const groupLine = CORE_NEEDS.map((g) => ({ g, n: ticked.filter((t) => t.startsWith(`${g.key}:`)).length })).filter((x) => x.n > 0).sort((a, b) => b.n - a.n).map((x) => `${x.g.label}: ${x.n}`).join(" &middot; ");
  const topNeeds = Object.entries(needCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([n, c]) => `<span class="chip" style="background:#F8E5DC;color:#96502F;">${esc(needLabel(n))} (${c})</span>`).join("");
  const ideas = latestIdeas(sessions, entries, 8);
  const ideasHtml = ideas.length ? `<h3 style="margin:14px 0 6px;font-size:13px;color:#33475C">Ideas the client chose for meeting these needs</h3>${ideas.map((v) => `<div class="st-reflect" style="margin:4px 0 8px"><b>${esc(v.need)}</b>: ${v.ideas.map(esc).join(" &middot; ")}</div>`).join("")}` : "";
  const needsHtml = ticked.length ? `<h2>Core needs most often hurt</h2><p class="lead">${groupLine}</p><div>${topNeeds}</div>${ideasHtml}` : "";
  const ownScripts = entries.flatMap((e) => e.flips.filter((f) => f.ownScript?.trim()).map((f) => ({ e, f }))).slice(0, 8).map(({ e, f }) => `<div class="st-reflect" style="margin:6px 0 10px"><b>${fmt(e.timestamp)} &middot; ${f.schema}</b>: &ldquo;${esc(f.ownScript.trim())}&rdquo;</div>`).join("");
  const ownHtml = ownScripts ? `<h2>Adaptive scripts in their own words</h2>${ownScripts}` : "";
  const plans = entries.flatMap((e) => e.flips.map((f) => ({ e, f }))).filter(({ f }) => f.facts.trim() || f.action.trim() || f.coach).slice(0, 10).map(
    ({ e, f }) => `<tr>
        <td class="c-when">${fmt(e.timestamp)}</td>
        <td><div class="st-schema">${f.schema}</div><div class="st-def">${copingStyleLabel(f.copingStyle) ?? ""}</div></td>
        <td>${f.story.trim() ? `<div class="st-note">Story: ${esc(f.story)}</div>` : ""}${f.facts.trim() ? `<div>Facts: ${esc(f.facts)}</div>` : ""}${f.coach ? `<div class="st-insight"><b>Flip Coach:</b> ${esc(f.coach)}</div>` : ""}</td>
        <td>${f.action.trim() ? `${esc(f.action)}${f.done ? " &#10003;" : ""}` : "&mdash;"}</td>
      </tr>`
  ).join("");
  return `
    ${entries.length ? `<h2>Flip the Script &mdash; schemas &amp; adaptive shifts</h2>
    <p class="lead">${st.total} schema${st.total === 1 ? "" : "s"} worked through &middot; ${st.done}/${st.actions} opposite actions completed &middot; Coping styles: ${styles}</p>
    <div>${top}</div>` : ""}
    ${plans ? `<table><colgroup><col style="width:17%" /><col style="width:23%" /><col style="width:38%" /><col style="width:22%" /></colgroup>
      <thead><tr><th>When</th><th>Schema</th><th>Reality test &amp; coach reframe</th><th>Opposite action</th></tr></thead><tbody>${plans}</tbody></table>` : ""}
    ${needsHtml}
    ${ownHtml}
    ${cards.length ? `<h2>Saved script cards</h2>${cards.map((c) => `<div class="st-reflect" style="margin:6px 0 10px"><b>${esc(c.title)}</b> (${esc(c.schema)}): &ldquo;${esc(c.text)}&rdquo;</div>`).join("")}` : ""}`;
}
function buildHtml(sessions, opts = {}, flips = [], cards = []) {
  const ordered = [...sessions].sort(
    (a, b) => a.timestamp.localeCompare(b.timestamp)
  );
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  const range = ordered.length > 0 ? `${fmt(first.timestamp)} \u2014 ${fmt(last.timestamp)}` : "\u2014";
  const deltas = ordered.filter((s) => s.postSuds != null && s.preSuds != null).map((s) => s.preSuds - s.postSuds);
  const avgDelta = deltas.length > 0 ? (deltas.reduce((a, b) => a + b, 0) / deltas.length).toFixed(1) : "\u2014";
  const fourFCounts = {
    fight: 0,
    flight: 0,
    freeze: 0,
    fawn: 0
  };
  ordered.forEach((s) => {
    if (s.survivalResponse) fourFCounts[s.survivalResponse] += 1;
  });
  const copingCounts = {};
  ordered.forEach(
    (s) => s.copingMechanisms.forEach((c) => {
      copingCounts[c] = (copingCounts[c] ?? 0) + 1;
    })
  );
  const topCoping = Object.entries(copingCounts).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} (${v})`).join(", ");
  const triggered = ordered.filter((s) => s.triggered).length;
  const moodLifts = ordered.filter((s) => s.moodBefore != null && s.moodNow != null).map((s) => s.moodNow - s.moodBefore);
  const avgMoodLift = moodLifts.length > 0 ? (moodLifts.reduce((a, b) => a + b, 0) / moodLifts.length).toFixed(1) : "\u2014";
  const chip = (bg, fg, text) => `<span class="chip" style="background:${bg};color:${fg};">${text}</span>`;
  const fourFChips = Object.keys(fourFCounts).map((k) => chip(STATE_HEX[k], "#fff", `${fourFConfig(k).label}: ${fourFCounts[k]}`)).join("");
  const schemaCounts = {};
  ordered.forEach((s) => {
    if (s.schemaState) schemaCounts[s.schemaState] = (schemaCounts[s.schemaState] ?? 0) + 1;
  });
  const schemaChips = Object.entries(schemaCounts).sort((a, b) => b[1] - a[1]).map(([k, v]) => chip("#DCE8F8", "#2F5A96", `${k}: ${v}`)).join("");
  const modeCounts = {};
  ordered.forEach((s) => {
    if (s.copingMode) modeCounts[s.copingMode] = (modeCounts[s.copingMode] ?? 0) + 1;
  });
  const modeChips = Object.entries(modeCounts).sort((a, b) => b[1] - a[1]).map(([k, v]) => chip("#EAF1FC", "#3B6CB0", `${k}: ${v}`)).join("");
  const rows = [...ordered].reverse().map((s) => {
    const delta = s.path === "morning" ? "Morning check" : s.preSuds == null ? "&mdash;" : s.postSuds != null ? `<b style="color:#2F5A96;">${s.preSuds} &rarr; ${s.postSuds}</b>` : `${s.preSuds}`;
    const fourF = s.survivalResponse ? `<span style="color:${STATE_HEX[s.survivalResponse]};font-weight:700;">&#9679; ${fourFConfig(
      s.survivalResponse
    ).label}</span>` : `<span style="color:#999;">&mdash;</span>`;
    const schema = s.schemaState ? `<div class="st-schema">${s.schemaState}</div>${schemaMeaning(s.schemaState) ? `<div class="st-def">${schemaMeaning(s.schemaState)}</div>` : ""}` : "";
    const mode = s.copingMode ? `<div class="st-mode">${s.copingMode}</div>${modeMeaning(s.copingMode) ? `<div class="st-def">${modeMeaning(s.copingMode)}</div>` : ""}` : "";
    const ema = s.emaSchemas && s.emaSchemas.length > 0 ? `<div class="st-ema">Schemas: ${s.emaSchemas.join(", ")}</div>` : "";
    const state = `${fourF}${schema}${mode}${ema}`;
    const coping = s.copingMechanisms.length > 0 ? s.copingMechanisms.join(", ") : "\u2014";
    const insight = s.aiInsight ? `<div class="st-insight">${s.aiInsight}</div>` : "";
    const moodBit = s.moodNow != null ? (() => {
      const now = moodLevel(s.moodNow);
      if (s.moodBefore != null) {
        const before = moodLevel(s.moodBefore);
        const d = s.moodNow - s.moodBefore;
        const dTxt = d > 0 ? `+${d}` : `${d}`;
        return `<div class="st-mood">Mood: ${before?.label ?? s.moodBefore} (${s.moodBefore > 0 ? "+" : ""}${s.moodBefore}) &rarr; ${now?.label ?? s.moodNow} (${s.moodNow > 0 ? "+" : ""}${s.moodNow}) &middot; ${dTxt}</div>`;
      }
      return `<div class="st-mood">Mood logged: ${now?.label ?? s.moodNow} (${s.moodNow > 0 ? "+" : ""}${s.moodNow})</div>`;
    })() : "";
    const moodNoteBit = s.moodNote ? `<div class="st-note">&ldquo;${s.moodNote}&rdquo;</div>` : "";
    const moodBeforeNoteBit = s.moodBeforeNote ? `<div class="st-note">After trigger: &ldquo;${s.moodBeforeNote}&rdquo;</div>` : "";
    const reflect = s.reflectionNotes ? `<div class="st-reflect"><b>Your note:</b> ${s.reflectionNotes}</div>` : "";
    const trig = s.triggered ? "Yes" : s.triggered === false ? "No" : "\u2014";
    const note = s.triggerNote ? `<div class="st-note">&ldquo;${s.triggerNote}&rdquo;</div>` : "";
    return `<tr>
        <td class="c-when">${fmt(s.timestamp)}${note}</td>
        <td class="c-center">${delta}</td>
        <td class="c-center">${trig}</td>
        <td class="c-state">${state}</td>
        <td class="c-coping">${unfinishedBit(s)}${coping}${moodBit}${moodBeforeNoteBit}${moodNoteBit}${insight}${reflect}</td>
      </tr>`;
  }).join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
  <meta name="viewport" content="width=1123, initial-scale=1.0" />
  <style>
    @page { size: A4 landscape; margin: 34px 32px; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { width: 100%; margin: 0; padding: 0; }
    body { font-family: -apple-system, Roboto, Helvetica, Arial, sans-serif; color:#1B2A3B; font-size:14px; line-height:1.55; }

    .header { display:flex; align-items:center; justify-content:space-between; border-bottom:3px solid #5E8FD6; padding-bottom:16px; margin-bottom:24px; }
    .brand { display:flex; align-items:center; gap:14px; }
    .brand img.logo { width:66px; height:66px; object-fit:contain; }
    .brand .bn { font-size:26px; font-weight:800; color:#2F5A96; letter-spacing:.3px; }
    .brand .tag { font-size:12px; color:#7C8DA1; margin-top:2px; }
    .client { display:flex; align-items:center; gap:12px; }
    .client .avatar { width:54px; height:54px; border-radius:27px; object-fit:cover; border:2px solid #D3E1F5; }
    .client .avatarPh { width:54px; height:54px; border-radius:27px; background:#EAF1FC; border:2px solid #D3E1F5; display:flex; align-items:center; justify-content:center; color:#5E8FD6; font-weight:800; font-size:22px; }
    .htext { text-align:right; }
    .htext h1 { font-size:20px; margin:0; color:#2F5A96; font-weight:700; }
    .htext .cn { font-size:15px; color:#1B2A3B; font-weight:700; margin-top:3px; }
    .htext .sub { color:#7C8DA1; font-size:12.5px; margin-top:3px; }

    .grid { display:flex; gap:18px; margin-bottom:26px; }
    .card { flex:1; background:#EAF1FC; border:1px solid #D3E1F5; border-radius:14px; padding:18px 20px; }
    .card .n { font-size:36px; font-weight:800; color:#2F5A96; line-height:1; }
    .card .l { font-size:12px; color:#3B6CB0; text-transform:uppercase; letter-spacing:.7px; margin-top:8px; }

    h2 { font-size:17px; color:#2F5A96; margin:26px 0 12px; padding-left:11px; border-left:4px solid #5E8FD6; page-break-after:avoid; }
    .chip { display:inline-block; margin:4px 9px 4px 0; padding:7px 15px; border-radius:999px; font-size:14px; font-weight:600; }
    .muted { color:#98A4B2; font-size:14px; }
    .lead { font-size:15px; color:#42546A; margin:0; }

    table { width:100%; border-collapse:collapse; font-size:14px; table-layout:fixed; page-break-inside:auto; margin-top:6px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    th { text-align:left; background:#2F5A96; color:#fff; padding:13px 14px; font-size:14px; font-weight:600; }
    td { padding:13px 14px; border-bottom:1px solid #E1E8F1; vertical-align:top; word-break:break-word; }
    tbody tr:nth-child(even) td { background:#F5F9FE; }
    .c-center { text-align:center; }
    .c-when { font-weight:700; color:#2F5A96; }
    .c-coping { color:#42546A; }
    .st-schema { font-size:14px; color:#2F5A96; font-weight:600; margin-top:5px; }
    .st-mode { font-size:14px; color:#3B6CB0; font-weight:600; margin-top:5px; }
    .st-def { font-size:12.5px; color:#7C8DA1; font-style:italic; margin-top:2px; }
    .st-ema { font-size:12.5px; color:#5E8FD6; margin-top:6px; }
    .st-note { font-size:13px; color:#2F5A96; font-style:italic; margin-top:5px; }
    .st-insight { font-size:13px; color:#5A6B7D; font-style:italic; margin-top:8px; line-height:1.55; }
    .st-mood { font-size:13px; color:#2E7D52; font-weight:600; margin-top:8px; }
    .st-reflect { font-size:13px; color:#2F5A96; margin-top:8px; line-height:1.55; }

    .st-unfinished { font-size:12.5px; color:#96502F; background:#FBEDE4; border-left:3px solid #E0A27C; border-radius:6px; padding:6px 10px; margin:0 0 8px; font-weight:600; }
    .prep { background:#F8E5DC; border:1px solid #EFCDB9; border-radius:14px; padding:14px 18px; margin:0 0 22px; }
    .prep-h { font-size:13px; font-weight:700; color:#96502F; text-transform:uppercase; letter-spacing:.6px; }
    .prep-t { font-size:15px; color:#1B2A3B; margin-top:6px; white-space:pre-line; }
    .week { background:#EAF1FC; border:1px solid #D3E1F5; border-radius:14px; padding:14px 18px; margin:0 0 22px; }
    .week-h { font-size:13px; font-weight:700; color:#2F5A96; text-transform:uppercase; letter-spacing:.6px; }
    .week-t { font-size:14.5px; color:#1B2A3B; margin-top:6px; white-space:pre-line; line-height:1.6; }
    .conv { border:1px solid #E1E8F1; border-radius:12px; padding:12px 16px; margin:0 0 10px; page-break-inside:avoid; }
    .conv-m { font-size:12px; color:#7C8DA1; font-weight:600; }
    .conv-t { font-size:14px; color:#1B2A3B; margin-top:4px; white-space:pre-line; }
    .conv-r { margin:10px 0 0 18px; padding:8px 12px; background:#F5F9FE; border-left:3px solid #5E8FD6; border-radius:8px; font-size:13.5px; color:#33475C; white-space:pre-line; }
    .foot { margin-top:26px; padding-top:14px; border-top:1px solid #E1E8F1; font-size:11.5px; color:#9AA7B5; }
  </style></head><body>
    <div class="header">
      <div class="brand">
        <img class="logo" src="${window.location.origin}/logo.png" />
        <div>
          <div class="bn">Triggerific-Pilot</div>
          <div class="tag">Regulate. Reflect. Recover.</div>
        </div>
      </div>
      <div class="htext">
        <h1>${opts.title ?? "Clinical Summary"}</h1>
        ${opts.clientName ? `<div class="cn">${opts.clientName}</div>` : ""}
        <div class="sub">${range}</div>
      </div>
      <div class="client">
        ${opts.avatar ? `<img class="avatar" src="${opts.avatar}" />` : `<div class="avatarPh">${(opts.clientName ?? "?").trim().charAt(0).toUpperCase() || "?"}</div>`}
      </div>
    </div>

    ${opts.prepNote ? `<div class="prep"><div class="prep-h">What ${opts.clientName ? opts.clientName.split(" ")[0] : "the client"} would like to talk about next session</div><div class="prep-t">${opts.prepNote.replace(/</g, "&lt;")}</div></div>` : ""}
    ${opts.weeklySummary ? `<div class="week"><div class="week-h">The client's week, in brief &mdash; the AI recap they read in the app (last 7 days)</div><div class="week-t">${opts.weeklySummary.replace(/</g, "&lt;")}</div></div>` : ""}
    <div class="grid">
      <div class="card"><div class="n">${ordered.length}</div><div class="l">Check-ins</div></div>
      <div class="card"><div class="n">${triggered}</div><div class="l">Triggered events</div></div>
      <div class="card"><div class="n">${avgDelta}</div><div class="l">Avg SUDS drop</div></div>
      <div class="card"><div class="n">${avgMoodLift}</div><div class="l">Avg mood lift</div></div>
    </div>

    ${unfinishedSummary(ordered)}
    ${notesHtml(opts.notes, opts.clientName?.split(" ")[0])}
    ${overrideTrendHtml(ordered)}
    ${triggerTrendsHtml(ordered)}

    <h2>4-F survival response profile</h2>
    <div>${fourFChips}</div>

    <h2>Schema states</h2>
    <div>${schemaChips || '<span class="muted">None recorded</span>'}</div>

    ${flipHtml(flips, cards, sessions)}

    <h2>Coping modes</h2>
    <div>${modeChips || '<span class="muted">None recorded</span>'}</div>

    <h2>Coping mechanism trends</h2>
    <p class="lead">${topCoping || "\u2014"}</p>

    <h2>Timeline</h2>
    <table>
      <colgroup>
        <col style="width:17%" /><col style="width:9%" /><col style="width:9%" /><col style="width:24%" /><col style="width:41%" />
      </colgroup>
      <thead><tr><th>When</th><th>SUDS</th><th>Triggered</th><th>State</th><th>Coping &amp; reflection</th></tr></thead>
      <tbody>${rows || `<tr><td colspan="5" style="text-align:center;color:#999;">No entries</td></tr>`}</tbody>
    </table>

    <p class="foot">Generated by Triggerific-Pilot &middot; Self-reported data for clinical discussion &middot; Not a diagnostic tool.</p>
  </body></html>`;
}
export { buildHtml };
